import { styled as muiStyled } from '@mui/material'
import { Theme } from '@mui/material/styles'
import Tab, { TabProps as TabPropsBase } from '@mui/material/Tab'
import TabsBase, { TabsProps as TabsPropsBase } from '@mui/material/Tabs'
import { Box, SxProps } from '@mui/system'
import { omit } from 'lodash-es'
import React, { useCallback, useEffect, useId, useRef } from 'react'
import { matchRoutes, useLocation, useNavigate } from 'react-router'
import { useTracking } from 'react-tracking'

import { boxShadowMedium } from '@/_vars'
import { TabPanelContext, TabPanelProps } from '@/atoms/Tabs/TabPanel'
import { theme } from '@/css-helpers/mui-theme'
import { TestId } from '@/lib/e2e-utils'
import { mobile } from '@/lib/mui/muiUtils'
import { eventStopper, scrollTo } from '@/lib/utils'
import { brightTurquoise, white } from '@/refactor/colors'

type TabProps<T extends string> = {
	value: T
	label: React.ReactNode
	content: (() => React.ReactNode) | null
} & Omit<TabPropsBase, 'content' | 'label' | 'value'> &
	Partial<TestId>

export type TabsProps<T extends string = string> = {
	value: T | false
	children: TabProps<T>[]
	onSetPanelIds?: (ids: TabPanelProps) => void
	onChange?: (value: T) => void
	fallback?: React.ReactNode
	dontScrollTop?: boolean
	contentProps?: {
		sx?: SxProps
		className?: string
	}
} & Partial<TestId> &
	Omit<TabsPropsBase, 'children' | 'onChange' | 'value'>

export function Tabs<T extends string>({
	children,
	onChange,
	onSetPanelIds,
	fallback,
	dontScrollTop,
	contentProps,
	value,
	orientation,
	variant,
	...rest
}: TabsProps<T>) {
	const tabsId = useId()
	const tabPanelId = useId()

	const activeTabIdx = children.findIndex((child) => child.value === value)
	const ariaLabelledby = `${tabsId}${activeTabIdx}`

	useEffect(() => {
		onSetPanelIds?.({
			id: tabPanelId,
			ariaLabelledby,
		})
	}, [tabsId, tabPanelId, activeTabIdx, onSetPanelIds, ariaLabelledby])

	const tabsRef = useJustifyTabs()

	const getContent = () => {
		const content = children.at(activeTabIdx)!.content
		return content === null ? content : React.createElement(content)
	}
	const content = activeTabIdx === -1 ? fallback : getContent()

	return (
		<>
			<TabsBase
				ref={tabsRef}
				{...rest}
				value={value}
				orientation={orientation}
				onChange={(e, value: T) => {
					if (isOpenInNewTab(e as React.MouseEvent)) {
						return
					}

					onChange?.(value)

					if (!dontScrollTop) {
						scrollTo({ y: 0 })
					}
				}}
				variant={variant ?? 'scrollable'}
				visibleScrollbar={true}
				scrollButtons={false}
				slots={{ indicator: () => null }}
				sx={{
					...(orientation === 'vertical'
						? {
								px: 0,
								'.MuiTab-root': {
									borderWidth: 0,
									maxWidth: 'unset',
									minHeight: theme.spacing(10),
									justifyContent: 'flex-start',
									padding: theme.spacing(1.2, 4),
									'&:hover': {
										color: brightTurquoise,
									},
									'&.Mui-selected': {
										color: white,
										backgroundColor: brightTurquoise,
									},
								},
							}
						: { px: { xs: 2, lg: 0 } }),
					...rest.sx,
				}}
			>
				{children.map(({ content, ..._rest }, i) => (
					<Tab
						{..._rest}
						key={_rest.value}
						id={`${tabsId}${i}`}
						aria-controls={i === activeTabIdx ? tabPanelId : undefined}
						disableRipple
					/>
				))}
			</TabsBase>
			{!!content && (
				<Box role="tabpanel" id={tabPanelId} aria-labelledby={ariaLabelledby} {...contentProps}>
					{content}
				</Box>
			)}
		</>
	)
}

export type AhrefTabProps<T extends string = string> = Omit<TabProps<T>, 'value' | 'content'> & {
	href: T
	matchPaths?: T[]
	analyticsKey?: string
	shouldRefreshOnSamePath?: boolean
}

export type AhrefTabsProps<T extends string = string> = {
	id: string
	children: AhrefTabProps<T>[]
} & Pick<TabsProps, 'centered' | 'data-test' | 'fallback' | 'orientation' | 'sx' | 'variant'>

export function AhrefTabs<T extends string>({ id, children, ...rest }: AhrefTabsProps<T>) {
	const location = useLocation()
	const navigate = useNavigate()

	const { setTabPanelProps } = React.useContext(TabPanelContext)

	const { trackEvent } = useTracking()

	const onClick = (e: React.MouseEvent<HTMLDivElement>, tab: AhrefTabProps<T>) => {
		const openInNewTab = isOpenInNewTab(e)
		if (openInNewTab) {
			window.open(tab.href, '_blank')
		}

		if (tab.analyticsKey) {
			trackEvent({
				action: 'Navigation Click',
				'Navigate To': tab.analyticsKey,
				'New Tab': openInNewTab,
			})
		}

		if (tab.shouldRefreshOnSamePath && matchRoutes([{ path: tab.href }], location.pathname)) {
			window.location.href = tab.href
		}
	}

	const activeTabIdx = children.findIndex(
		(child) =>
			matchRoutes([{ path: child.href }], location.pathname) ||
			matchRoutes(child.matchPaths?.map((path) => ({ path })) ?? [], location.pathname),
	)

	const value = activeTabIdx === -1 ? false : (location.pathname as T)

	return (
		<Tabs
			{...rest}
			value={value}
			onChange={navigate}
			onSetPanelIds={useCallback(
				(tabPanelProps: TabPanelProps) => setTabPanelProps(id, tabPanelProps),
				[id, setTabPanelProps],
			)}
			role="navigation"
		>
			{children.map((child, i) => {
				const _child = replaceHref(child)

				return {
					...omit(_child, ['analyticsKey', 'shouldRefreshOnSamePath', 'matchPaths']),
					onClick: eventStopper.preventDefault((e) => onClick(e, _child as AhrefTabProps<T>)),
					component: 'a',
					content: null,
					'aria-current': i === activeTabIdx ? 'page' : undefined,
				} as TabProps<T>

				function replaceHref(child: AhrefTabProps<T>) {
					const href = i === activeTabIdx ? value : child.href

					return { ...child, value: href, href }
				}
			})}
		</Tabs>
	)
}

export const AhrefSubTabs = muiStyled(AhrefTabs)(getSubTabsProps)

export const SubTabs = muiStyled(Tabs)((p) => ({
	...getSubTabsProps(p),
	'.MuiTabs-list': {
		padding: p.theme.spacing(0, 6),
		[mobile(p)]: {
			padding: p.theme.spacing(0, 3),
		},
		justifyContent: 'flex-start',
	},
}))

function getSubTabsProps(p: { theme: Theme }) {
	return {
		boxShadow: boxShadowMedium,
		borderRadius: 5,
		// when Y scroll appears the tab highlight is not visible, so I have to make tabs higher
		[mobile(p)]: {
			minHeight: 52,
		},
	}
}

/**
 * Alternative for CSS "scroll-state" and "scrollable" which is not supported by all browsers
 */
function useJustifyTabs() {
	const tabsRef = useRef<HTMLDivElement>(null)
	const element = tabsRef.current

	const updateStyle = useCallback(
		function () {
			const scroller = element?.querySelector('.MuiTabs-scroller')

			if (!scroller) {
				return
			}

			;(scroller.querySelector('.MuiTabs-list') as HTMLElement).style.setProperty(
				'justify-content',
				isHorizontallyScrollable(scroller) ? 'flex-start' : '',
			)
		},
		[element],
	)

	useEffect(updateStyle, [updateStyle])

	useEffect(() => {
		window.addEventListener('resize', updateStyle)
		return () => window.removeEventListener('resize', updateStyle)
	}, [updateStyle])

	return tabsRef
}

function isOpenInNewTab(e: React.MouseEvent) {
	return e.ctrlKey || e.metaKey || e.button === 1
}

function isHorizontallyScrollable(el: Element) {
	return el.scrollWidth > el.clientWidth
}
